import { useEffect, useRef } from 'react'
import type { PDFDocumentProxy } from 'pdfjs-dist'
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url'

const DECK_URL = '/rocket-presentation/deck-base.pdf'

let deckPromise: Promise<PDFDocumentProxy> | null = null

function loadDeck() {
  if (!deckPromise) {
    deckPromise = import('pdfjs-dist/build/pdf.mjs')
      .then((pdfjs) => {
        pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker
        return pdfjs.getDocument({ url: DECK_URL }).promise
      })
      .catch((error: unknown) => {
        deckPromise = null
        throw error
      })
  }
  return deckPromise
}

/** Página do PDF original, desenhada nítida no tamanho da tela. */
export function RocketPdfSlide({ pageNumber }: { pageNumber: number }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const wrap = wrapRef.current
    const canvas = canvasRef.current
    if (!wrap || !canvas) return

    let cancelled = false
    let rendering = false
    let pending = false

    const paint = async () => {
      if (cancelled) return
      if (rendering) {
        pending = true
        return
      }
      const width = wrap.clientWidth
      const height = wrap.clientHeight
      if (width < 40 || height < 40) return
      rendering = true
      try {
        const pdf = await loadDeck()
        const page = await pdf.getPage(pageNumber)
        if (cancelled) return
        const base = page.getViewport({ scale: 1 })
        const fit = Math.min(width / base.width, height / base.height)
        const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
        const viewport = page.getViewport({ scale: fit * pixelRatio })
        canvas.width = Math.floor(viewport.width)
        canvas.height = Math.floor(viewport.height)
        canvas.style.width = `${Math.floor(base.width * fit)}px`
        canvas.style.height = `${Math.floor(base.height * fit)}px`
        const context = canvas.getContext('2d')
        if (!context) return
        await page.render({ canvasContext: context, viewport }).promise
      } finally {
        rendering = false
        if (pending && !cancelled) {
          pending = false
          void paint()
        }
      }
    }

    void paint()
    const observer = new ResizeObserver(() => {
      void paint()
    })
    observer.observe(wrap)
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [pageNumber])

  return (
    <div ref={wrapRef} className="flex h-full w-full items-center justify-center">
      <canvas ref={canvasRef} className="max-h-full max-w-full" />
    </div>
  )
}
